/** Vue du connecteur Administrateur ; aucun secret, état OAuth ou identifiant Google technique. */
export interface GoogleDriveIntegrationView {
  readonly configured: boolean;
  readonly connection: {
    readonly backupMetadataGranted?: boolean;
    readonly status: 'VERIFIED' | 'DISABLED';
    readonly accountEmail: string;
    readonly verifiedAt: string;
    readonly limitBytes: string | null;
    readonly usageBytes: string | null;
  } | null;
  readonly attempt: {
    readonly id: string;
    readonly status:
      'PENDING' | 'RECEIVED' | 'EXCHANGING' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'EXPIRED';
    readonly expiresAt: string;
  } | null;
  readonly history: readonly GoogleDriveHistoryEntry[];
}

export type GoogleDriveHistoryEntry =
  | {
      readonly kind: 'CLIENT_FOLDERS';
      readonly id: string;
      readonly clientId: string;
      readonly clientName: string;
      readonly status: GoogleDriveClientFolderCheckView['status'];
      readonly createdAt: string;
    }
  | {
      readonly kind: 'VIDEO_CHECK';
      readonly id: string;
      readonly status: GoogleDriveVideoProbeView['status'];
      readonly createdAt: string;
    }
  | {
      /** Absent sur les anciennes réponses du connecteur pendant une livraison. */
      readonly kind?: 'CONNECTION';
      readonly id: string;
      readonly status: string;
      readonly createdAt: string;
    }
  | {
      readonly kind: 'STORAGE_CHECK';
      readonly id: string;
      readonly status: 'RUNNING' | 'VERIFIED' | 'NOT_VERIFIED' | 'INTERRUPTED' | 'CLEANED';
      readonly createdAt: string;
      readonly recovering: boolean;
      readonly imported: boolean;
      readonly results: GoogleDriveStorageCheckResults;
    };

export interface GoogleDriveAuthorizationStart {
  readonly authorizationUrl: string;
  readonly expiresAt: string;
}

/** Constat Tosho d'un commit déjà confirmé. Aucun ID Google, empreinte de
 * requête, bail ou URI ; ne vaut pas nouvelle preuve d'accès au média. */
export interface DriveFileVersionConfirmationView {
  readonly versionId: string;
  readonly fileId: string;
  readonly sequence: number;
  readonly state: 'VERIFIED';
}
/** Annulation de préparation avant START, jamais suppression d'un média Google. */
export interface DriveFileVersionCancellationView {
  readonly versionId: string;
  readonly fileId: string;
  readonly sequence: number;
  readonly state: 'CANCELLED';
}

/** Résultats datés du fichier synthétique ; aucun identifiant Google ni contenu média. */
export interface GoogleDriveStorageCheckResults {
  readonly privateWriteVerified: boolean;
  readonly fullReadVerified: boolean;
  readonly rangeReadVerified: boolean;
  readonly trashRestoreVerified: boolean;
  readonly cleanupVerified: boolean;
}

export interface GoogleDriveStorageProbeView extends GoogleDriveStorageCheckResults {
  readonly status: 'RUNNING' | 'VERIFIED' | 'NOT_VERIFIED';
  readonly checkedAt: string | null;
}

/** Préparation technique, jamais une preuve de décodage Safari ou de droits Client. */
export interface GoogleDriveVideoProbeView {
  readonly id: string;
  readonly status: 'PREPARING' | 'READY' | 'NOT_VERIFIED' | 'TRASHED';
  readonly checkedAt: string | null;
}
/** Constat daté de dossiers vides privés. Aucune capacité de dépôt ni identité Google exposée. */
export interface GoogleDriveClientFoldersView {
  readonly clientId: string;
  readonly folderReference: string;
  readonly checkedAt: string;
}

/** Dernière tentative conservée, y compris après erreur ou arrêt du processus. */
export interface GoogleDriveClientFolderCheckView {
  readonly id: string;
  readonly clientId: string;
  readonly status: 'RUNNING' | 'VERIFIED' | 'NOT_VERIFIED' | 'INTERRUPTED';
  readonly checkedAt: string | null;
}

/** Diagnostic technique Administrateur à liste fermée. Jamais de valeur fournisseur libre. */
export const DRIVE_FOLDER_DIAGNOSTIC_STEPS = [
  'PREPARE',
  'CLIENT_SETUP',
  'REGISTRY',
  'ROOT_READ',
  'ID_RESERVATION',
  'FOLDER_LOOKUP',
  'FOLDER_CREATE',
  'FINALIZE',
] as const;
export const DRIVE_FOLDER_DIAGNOSTIC_REASONS = [
  'HTTP',
  'TRANSPORT',
  'CONTENT',
  'ACCESS',
  'TOKEN',
  'INTERRUPTED',
  'METADATA',
  'INTERNAL',
] as const;
export const DRIVE_FOLDER_DIAGNOSTIC_CHECKS = [
  'FORMAT',
  'IDENTITY',
  'NAME',
  'TYPE',
  'TRASH',
  'OWNER',
  'PARENT',
  'MARKER',
  'ACL',
] as const;
export interface GoogleDriveClientFolderDiagnostic {
  readonly step: (typeof DRIVE_FOLDER_DIAGNOSTIC_STEPS)[number];
  readonly reason: (typeof DRIVE_FOLDER_DIAGNOSTIC_REASONS)[number];
  readonly status?: number;
  readonly check?: (typeof DRIVE_FOLDER_DIAGNOSTIC_CHECKS)[number];
}

/** Reconstituer exclusivement les champs connus, sans convertir une erreur libre en diagnostic. */
export function readDriveClientFolderDiagnostic(
  value: unknown,
): GoogleDriveClientFolderDiagnostic | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  if (
    Object.keys(row).some((key) => !['step', 'reason', 'status', 'check'].includes(key)) ||
    !DRIVE_FOLDER_DIAGNOSTIC_STEPS.some((step) => step === row.step) ||
    !DRIVE_FOLDER_DIAGNOSTIC_REASONS.some((reason) => reason === row.reason) ||
    (row.status !== undefined &&
      (row.reason !== 'HTTP' ||
        !Number.isInteger(row.status) ||
        Number(row.status) < 100 ||
        Number(row.status) > 599)) ||
    (row.reason === 'HTTP' && row.status === undefined) ||
    (row.check !== undefined &&
      (row.reason !== 'METADATA' ||
        !DRIVE_FOLDER_DIAGNOSTIC_CHECKS.some((check) => check === row.check)))
  )
    return null;
  return {
    step: row.step as GoogleDriveClientFolderDiagnostic['step'],
    reason: row.reason as GoogleDriveClientFolderDiagnostic['reason'],
    ...(row.status === undefined ? {} : { status: row.status as number }),
    ...(row.check === undefined
      ? {}
      : { check: row.check as NonNullable<GoogleDriveClientFolderDiagnostic['check']> }),
  };
}

/** Constat ponctuel du quota global, sans identité Google ni autorisation de dépôt. */
export interface GoogleDriveQuotaView {
  readonly limitBytes: string | null;
  readonly usageBytes: string | null;
  readonly observedAt: string;
}

export interface GoogleDriveQuotaSettingsValues {
  readonly reserveBytes: string;
  readonly maximumFileBytes: string;
  readonly maxObservationAgeMs: number;
}
export interface GoogleDriveQuotaSettingsView extends GoogleDriveQuotaSettingsValues {
  readonly revision: number;
  readonly updatedAt: string;
}
export interface GoogleDriveQuotaSettingsSave {
  readonly expectedRevision: number | null;
  readonly settings: GoogleDriveQuotaSettingsValues;
}
